// mission-initializer-compatibility.ts
//
// Split off mission-initializer.ts (≤200 line budget): builds and writes
// `<control_root>/compatibility.json` (spec §18/Req 1, N36) after
// mission-workspace.ts has applied the settings profile.
//
// Judgment call (documented in evidence): CompatibilitySchema has TWO
// separate digest fields (settings_digest, profile_digest) but
// MissionWorkspaceResult only produces ONE combined digest
// (settingsProfileSha256, which already hashes settings+profile together).
// This module reuses that single value for BOTH fields rather than computing
// a second, narrower digest — the combined digest already changes whenever
// EITHER settings or profile changes, so it is a safe (if coarser) drift
// detector for doctor's N36 comparison. A future phase could split
// computeSettingsProfileDigest into two if finer-grained drift attribution
// (settings vs profile) becomes necessary.

import { join } from "node:path";
import { spawnAsyncCapture } from "../spawn-async-capture-with-timeout.js";
import { readPinnedPackVersion } from "../team-pack-manager.js";
import { writeFileDurable } from "./durable-file-writer.js";
import { type Compatibility, CompatibilitySchema } from "./schemas/compatibility-schema.js";

export interface BuildCompatibilityInput {
  missionWorkspace: string;
  controlRoot: string;
  cliVersion: string;
  settingsProfileSha256: string;
  permissionsAllowFallback: boolean;
  now?: () => Date;
  /** Injectable so tests never require a real `claude` binary on PATH. */
  probeClaudeVersion?: () => Promise<{ path: string; version: string; hash: string }>;
}

async function defaultProbeClaudeVersion(): Promise<{
  path: string;
  version: string;
  hash: string;
}> {
  try {
    const res = await spawnAsyncCapture("claude", ["--version"], { timeoutMs: 10_000 });
    const version = res.status === 0 ? res.stdout.trim() : "(unknown)";
    const which = await spawnAsyncCapture("which", ["claude"], { timeoutMs: 5_000 });
    const path = which.status === 0 ? which.stdout.trim() : "(unknown)";
    const { createHash } = await import("node:crypto");
    const hash = createHash("sha256").update(`${path}:${version}`, "utf8").digest("hex");
    return { path, version, hash };
  } catch {
    // claude binary unavailable in this environment (CI/test) — record a
    // placeholder rather than blocking init; doctor's own checks re-verify.
    const { createHash } = await import("node:crypto");
    return {
      path: "(unavailable)",
      version: "(unavailable)",
      hash: createHash("sha256").update("unavailable", "utf8").digest("hex"),
    };
  }
}

export async function writeCompatibilityManifest(
  input: BuildCompatibilityInput,
): Promise<Compatibility> {
  const now = input.now ?? (() => new Date());
  const probe = input.probeClaudeVersion ?? defaultProbeClaudeVersion;
  const claudeBinary = await probe();
  const packVersion = await readPinnedPackVersion(input.missionWorkspace).catch(() => "(unknown)");

  const manifest: Compatibility = CompatibilitySchema.parse({
    schema_version: 1,
    captured_at: now().toISOString(),
    cli_version: input.cliVersion,
    claude_binary: claudeBinary,
    pack: { version: packVersion, digest: `sha256:${input.settingsProfileSha256.slice(0, 16)}` },
    settings_digest: input.settingsProfileSha256,
    profile_digest: input.settingsProfileSha256,
    policy_digest: "sha256:not-yet-computed",
    contract_version: "autopilot/1",
    schema_versions: { mission: 1, ticket: 1, init: 1, directive: 1, compatibility: 1 },
    os: process.platform,
    runtime: `node/${process.version}`,
    supported_flags: ["--non-interactive", "--json", "--watch"],
    supported_output_events: [],
    command_adapter_ids: ["implement-v1"],
    smoke_evidence_paths: [],
    permissions_allow_fallback: input.permissionsAllowFallback,
  });

  const path = join(input.controlRoot, "compatibility.json");
  await writeFileDurable(path, Buffer.from(`${JSON.stringify(manifest, null, 2)}\n`, "utf8"));
  return manifest;
}
